'use client';

import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  Calendar,
  Clock,
  ShieldAlert,
  User,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from '@kit/shared/currency';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Badge } from '@kit/ui/badge';
import { Card, CardContent } from '@kit/ui/card';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { cn } from '@kit/ui/utils';

import { DetailsDrawer } from '~/(tenant)/_components/details-drawer';
import { useNativeLayout } from '~/(tenant)/_components/native/_lib/native-layout-context';
import { ParticipantsPreview } from '~/(tenant)/_components/participants-preview';
import { shouldShowSpotsLeft } from '~/(tenant)/_lib/class-scarcity';
import { selectClassTypeCopy } from '~/(tenant)/book/_lib/format-class-copy';
import { imageFocalPosition } from '~/lib/utils/image-focal-point';

import type { ClassSeasonOffer } from '../_lib/server/server-actions';
import { useNearViewport } from '../_lib/use-near-viewport';
import { useBookingPresentation } from './booking-presentation';
import styles from './native-booking-controls.module.css';
import { NativeClassCardSummary } from './native-class-card-summary';
import classStyles from './native-class-card.module.css';

export interface ClassCardProps {
  classData: {
    booking_id: string;
    class_name: string;
    class_name_alt?: string | null;
    class_description: string | null;
    class_description_alt?: string | null;
    image_url: string | null;
    image_focal_x?: number | null;
    image_focal_y?: number | null;
    trainer_name: string | null;
    trainer_avatar_url?: string | null;
    class_category_name?: string | null;
    class_category_name_alt?: string | null;
    start_time: string;
    end_time: string;
    max_capacity: number;
    enrolled_count: number;
    spots_remaining: number;
    price_per_person: number;
    waitlist_enabled: boolean;
    is_full: boolean;
    virtually_full: boolean;
    is_eligible: boolean | null;
    eligibility_reasons: string[];
    eligibility_requirements?: { label: string; value: string }[] | null;
    /** True when the signed-in viewer is already a live participant of this session. */
    is_already_enrolled?: boolean;
    /** class_types.show_spots_remaining — omitted means show (the DB default). */
    show_spots_remaining?: boolean;
    /** class_types.show_spots_remaining_last_only — omitted means always show (the DB default). */
    show_spots_remaining_last_only?: boolean;
    /** class_types.show_participants — omitted means hide (the DB default). */
    show_participants?: boolean;
    /** Sold only as this season (selecting it opens the season checkout). */
    season?: ClassSeasonOffer;
  };
  isSelected: boolean;
  onSelect: () => void;
  selectedDate: Date;
}

export function ClassCard({
  classData,
  isSelected,
  onSelect,
  selectedDate,
}: ClassCardProps) {
  const presentation = useBookingPresentation();
  const isNativeLayout = useNativeLayout();
  const { t, i18n } = useTranslation('bookings');
  const { t: tClasses } = useTranslation('classes');
  const supabase = useSupabase();
  const [imageError, setImageError] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Player list (avatar stack on the card + full list in the drawer). The RPC
  // fails closed server-side, but the listing already tells us whether the
  // template opted in — only fetch when it did, so the default case
  // (show_participants = false) costs zero round trips on the /book path.
  // Long lists (the class agenda) render many cards at once; only fetch the
  // players once a card is about to scroll into view.
  const showsParticipants = classData.show_participants === true;
  const [nearViewportRef, isNearViewport] =
    useNearViewport<HTMLDivElement>(showsParticipants);
  const { data: players = [] } = useQuery({
    queryKey: ['class-participants-public', classData.booking_id],
    staleTime: 60_000,
    enabled: showsParticipants && (isNearViewport || detailsOpen),
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_class_participants_public',
        { p_booking_id: classData.booking_id },
      );

      if (error) throw error;

      return (data ?? [])
        .filter((row) => !!row.participant_name)
        .map((row) => ({
          name: row.participant_name,
          pictureUrl: row.picture_url ?? null,
        }));
    },
  });
  const isAlreadyEnrolled = classData.is_already_enrolled === true;
  // Treat hard-full and virtually-full (waitlist notification window active) the same
  const isFull = classData.is_full || classData.virtually_full;
  // An already-enrolled viewer can neither re-book nor waitlist this session.
  const canJoinWaitlist =
    isFull && classData.waitlist_enabled && !isAlreadyEnrolled;
  // is_eligible tri-state: true = eligible, false = not eligible, null = rules exist but no userId provided
  // NOTE: In the admin-books-for-guest flow, userId is the admin's own id (not the guest's),
  // so the eligibility display reflects the admin's profile rather than the guest's.
  // This is a UX limitation only — server-side enforcement in the checkout action always
  // uses the actual enrollUserId (the guest), so security is not affected.
  const isIneligible = classData.is_eligible === false;
  const isEligibilityUnknown = classData.is_eligible === null;
  const isDisabled =
    isAlreadyEnrolled ||
    (isFull && !classData.waitlist_enabled) ||
    isIneligible;
  const hasImage = classData.image_url && !imageError;

  // The remaining count is opt-out per class template (show_spots_remaining);
  // the Already booked / Waitlist / Full states always render.
  const statusLabel = isAlreadyEnrolled
    ? t('alreadyBooked')
    : canJoinWaitlist
      ? t('joinWaitlist')
      : isFull
        ? t('full')
        : shouldShowSpotsLeft({
              spotsRemaining: classData.spots_remaining,
              showSpotsRemaining: classData.show_spots_remaining,
              lastSpotOnly: classData.show_spots_remaining_last_only,
            })
          ? `${classData.spots_remaining} ${classData.spots_remaining === 1 ? t('spot') : t('spots')} ${t('left')}`
          : null;

  // Pick the name/description for the active language, falling back to the
  // primary copy when the tenant hasn't authored a translation.
  const localized = selectClassTypeCopy(
    {
      name: classData.class_name,
      name_alt: classData.class_name_alt,
      description: classData.class_description,
      description_alt: classData.class_description_alt,
    },
    i18n.language,
  );
  const displayName = localized.name ?? classData.class_name;

  return (
    <div ref={nearViewportRef}>
      <Card
        data-selected={isSelected}
        className={cn(
          styles.optionCard,
          classStyles.card,
          'relative overflow-hidden transition-all',
          !isNativeLayout && isSelected && 'ring-primary ring-2',
          !isNativeLayout && !isDisabled && 'hover:shadow-md',
        )}
      >
        <CardContent className="p-0">
          <NativeClassCardSummary
            classData={classData}
            displayName={displayName}
            isSelected={isSelected}
            isDisabled={isDisabled}
            imageUrl={hasImage ? classData.image_url : null}
            onImageError={() => setImageError(true)}
            onSelect={onSelect}
            onDetails={() => setDetailsOpen(true)}
            statusLabel={statusLabel ?? undefined}
            status={
              isAlreadyEnrolled
                ? 'booked'
                : isFull
                  ? 'full'
                  : classData.spots_remaining <= 2
                    ? 'limited'
                    : 'available'
            }
            eligibilityMessage={
              isIneligible
                ? (classData.eligibility_reasons[0] ?? tClasses('notEligible'))
                : isEligibilityUnknown
                  ? tClasses('loginToCheckEligibility')
                  : undefined
            }
            eligibilityTone={isIneligible ? 'danger' : 'neutral'}
            players={players}
          />
          {classData.season ? (
            <div className="px-4 pb-3">
              <SeasonOfferLine season={classData.season} />
            </div>
          ) : null}
        </CardContent>

        <DetailsDrawer
          contentClassName={presentation.className}
          contentStyle={presentation.style}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          name={displayName}
          description={localized.description}
          imageUrl={hasImage ? classData.image_url : null}
          imagePosition={imageFocalPosition(
            classData.image_focal_x,
            classData.image_focal_y,
          )}
          footer={
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="text-muted-foreground flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>{t('classTime', { defaultValue: 'Class Time' })}</span>
                </div>
                <span className="font-medium">
                  {formatTimeToAmPm(classData.start_time, i18n.language)} -{' '}
                  {formatTimeToAmPm(classData.end_time, i18n.language)}
                </span>
              </div>

              <div className="flex items-center justify-between border-b pb-3">
                <div className="text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  <span>{t('classDate', { defaultValue: 'Class Date' })}</span>
                </div>
                <span className="font-medium">
                  {formatLocaleDate(selectedDate, i18n.language, {
                    weekday: 'long',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </span>
              </div>

              {classData.trainer_name && (
                <div className="flex items-center justify-between border-b pb-3">
                  <div className="text-muted-foreground flex items-center gap-2">
                    <User className="h-5 w-5" />
                    <span>
                      {t('classCoach', { defaultValue: 'Class Coach' })}
                    </span>
                  </div>
                  <span className="font-medium">{classData.trainer_name}</span>
                </div>
              )}

              {players.length > 0 && (
                <div className="border-b pb-3">
                  <div className="text-muted-foreground mb-1 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    <span>{t('playerList.label')}</span>
                  </div>
                  <ParticipantsPreview players={players} />
                </div>
              )}

              {classData.eligibility_requirements?.map((req) => (
                <div
                  key={req.label}
                  className="flex items-center justify-between border-b pb-3"
                >
                  <div className="text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    <span>{req.label}</span>
                  </div>
                  <span className="font-medium">{req.value}</span>
                </div>
              ))}

              {isIneligible && (
                <div className="border-destructive/30 bg-destructive/5 rounded-lg border p-3">
                  <div className="text-destructive mb-1 flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4" />
                    <h4 className="text-sm font-semibold">
                      {tClasses('notEligible')}
                    </h4>
                  </div>
                  {classData.eligibility_reasons.length > 0 ? (
                    <ul className="space-y-1">
                      {classData.eligibility_reasons.map((reason, i) => (
                        <li key={i} className="text-muted-foreground text-sm">
                          {reason}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {tClasses('completeProfileForEligibility')}
                    </p>
                  )}
                </div>
              )}

              {isEligibilityUnknown && (
                <div className="rounded-lg border border-amber-300/50 bg-amber-50/50 p-3 dark:bg-amber-950/20">
                  <div className="mb-1 flex items-center gap-2 text-amber-700 dark:text-amber-400">
                    <ShieldAlert className="h-4 w-4" />
                    <h4 className="text-sm font-semibold">
                      {tClasses('eligibilityRequiresLogin')}
                    </h4>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {tClasses('loginToCheckEligibilityDescription')}
                  </p>
                </div>
              )}
            </div>
          }
        />
      </Card>
    </div>
  );
}

/** "Season · 6 sessions left · ~~618.75~~ 309.38" — values from the server quote. */
function SeasonOfferLine({ season }: { season: ClassSeasonOffer }) {
  const { t, i18n } = useTranslation('bookings');
  const currency = useTenantCurrency();
  const format = (amount: number) =>
    formatCurrency(amount, currency, i18n.language, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div
      className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
      data-test="class-season-offer"
    >
      <Badge variant="info">{t('season.badge')}</Badge>
      <span className="text-muted-foreground">
        {t('season.sessionsLeft', { count: season.remainingSessions })}
      </span>
      {season.prorated ? (
        <span className="text-muted-foreground inline-flex items-center gap-1 line-through">
          <CurrencyIcon className="h-3 w-3" />
          {format(season.fullPrice)}
        </span>
      ) : null}
      <span className="inline-flex items-center gap-1 font-semibold">
        <CurrencyIcon className="h-3 w-3" />
        {format(season.price)}
      </span>
    </div>
  );
}
